import type { PlanCreateTab } from '../ui/templates/PlanCreateTemplate';

/**
 * Aba onde cada campo validado é editado.
 *
 * Só os campos que o `planCreateSchema` pode reprovar entram aqui — os três do
 * vínculo ficam de fora de propósito: eles vivem acima das abas, sempre
 * visíveis, e não exigem navegação para serem corrigidos.
 */
const FIELD_TAB: Readonly<Record<string, PlanCreateTab>> = {
  description: 'detalhes',
  discountScholarshipPercentage: 'detalhes',
  totalValue: 'valores',
  numberParcels: 'valores',
  paymentMethod: 'valores',
  expirationDate: 'valores',
};

/** Ordem de visita: a primeira aba com erro é a que o operador deve ver. */
const TAB_ORDER: readonly PlanCreateTab[] = ['detalhes', 'valores'];

/**
 * Primeira aba que contém um campo reprovado na validação.
 *
 * Dividir o formulário em abas cria um risco que o formulário contínuo não
 * tinha: acionar "Criar plano" com um erro na aba inativa não mostraria nada ao
 * operador — o botão pareceria quebrado. Esta função diz para onde levá-lo.
 *
 * @param errorFields - Nomes dos campos reprovados (`Object.keys(formState.errors)`).
 * @returns A primeira aba com erro, ou `null` quando os erros estão só no vínculo.
 *
 * @example
 * ```ts
 * findTabWithError(['totalValue']);            // 'valores'
 * findTabWithError(['description', 'totalValue']); // 'detalhes'
 * findTabWithError(['courseFinancialId']);     // null — vínculo está sempre visível
 * ```
 */
export function findTabWithError(errorFields: readonly string[]): PlanCreateTab | null {
  const tabsWithError = new Set(
    errorFields
      .map(field => FIELD_TAB[field])
      .filter((tab): tab is PlanCreateTab => tab !== undefined),
  );

  return TAB_ORDER.find(tab => tabsWithError.has(tab)) ?? null;
}
